import {
  convertDateToYYYYMMDDFormat,
  CustomDateRangePicker,
  PageWrapper,
  parseLocalDateFromYYYYMMDD,
  STATUSPAGE,
} from '@repo/ui';
import { GetAcademicOverviewData } from 'domain/usecases';
import { FormikValues } from 'formik';
import noDataBg from 'presentation/app/assets/images/no-task.png';
import { Badge, Card, Col, Label, Row, Spinner } from 'reactstrap';

import { AcademicOverviewCard } from '../../components/academic-overview-card';
import { ActivitiesByDayChart } from '../../components/activites-by-day-chart';
import { ReportsGeneralFilters } from '../../components/reports-general-filters';
import { StudentsByGroupChart } from '../../components/students-by-group-chart';

interface IProps {
  data?: GetAcademicOverviewData.Result;
  validation: FormikValues;
  loading?: STATUSPAGE;
}

export const AcademicOverviewView = ({ data, validation, loading }: IProps) => {
  return (
    <PageWrapper title="Gestão Acadêmica">
      <div className="mb-4">
        <h4>Gestão Acadêmica</h4>
        <p className="text-muted mb-0">
          Acompanhe indicadores e métricas gerais sobre grupos, estudantes e
          atividades acadêmicas.
        </p>
      </div>

      <Card>
        <ReportsGeneralFilters
          validation={validation}
          inputsChildren={
            <Col md={9} lg={6} xxl={4}>
              <Row>
                <Col lg={6} className="mb-3">
                  <Label htmlFor="initial-date" className="form-label">
                    Período:
                  </Label>
                  <CustomDateRangePicker
                    initialStartDate={parseLocalDateFromYYYYMMDD(
                      validation.values.starts,
                    )}
                    initialEndDate={parseLocalDateFromYYYYMMDD(
                      validation.values.ends,
                    )}
                    placement="auto"
                    onDateChange={(start, ends) => {
                      validation.setFieldValue(
                        'starts',
                        convertDateToYYYYMMDDFormat(start),
                      );
                      validation.setFieldValue(
                        'ends',
                        convertDateToYYYYMMDDFormat(ends),
                      );
                    }}
                  />
                </Col>
              </Row>
            </Col>
          }
          buttonsChildren={
            <button
              type="submit"
              disabled={loading === STATUSPAGE.LOADING && true}
              className="btn btn-primary waves-effect waves-light"
            >
              {loading === STATUSPAGE.LOADING ? (
                <Spinner size="sm">{['Carregando...']}</Spinner>
              ) : (
                <>
                  Consultar
                  <i className="ri-computer-line mx-2"></i>
                </>
              )}
            </button>
          }
        />
      </Card>

      {data && (
        <>
          <AcademicOverviewCard cardTitle="Resumo por Grupo">
            {/* <DataTable
              actions={[]}
              canSearch={data && data.groupResume.length > 0}
              columns={makeColumnsResumeByGroup()}
              data={data?.groupResume || []}
              loading={false}
              pagination={false}
            /> */}
            {data?.groupResume.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th scope="col">Grupo</th>
                      <th scope="col">Código</th>
                      <th scope="col">Professores</th>
                      <th scope="col">Preceptores</th>
                      <th scope="col">Estudantes</th>
                      <th scope="col">Realizadas</th>
                      <th scope="col">Perdidas</th>
                      <th scope="col">Perdidas Justificadas</th>
                      <th scope="col">Validadas</th>
                      <th scope="col">Perdidas Validadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.groupResume.map((row, index) => (
                      <tr key={index}>
                        <td>{row.group}</td>
                        <td>{row.code}</td>
                        <td>{row.teachers}</td>
                        <td>{row.preceptors}</td>
                        <td>{row.students}</td>
                        <td>{row.accomplished}</td>
                        <td>{row.lost}</td>
                        <td>{row.justifiedLost}</td>
                        <td>{row.validated}</td>
                        <td>{row.validatedLost}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <img src={noDataBg} alt="Sem dados" height={100} width={100} />
              </div>
            )}
          </AcademicOverviewCard>

          <AcademicOverviewCard cardTitle="Resumo por Tarefa">
            {/* <DataTable
              actions={[]}
              canSearch={data && data.taskResume.length > 0}
              columns={makeColumnsResumeByTask()}
              data={data?.taskResume || []}
              loading={false}
              pagination={false}
            /> */}
            {data?.taskResume.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th scope="col">Tarefa</th>
                      <th scope="col">Realizadas</th>
                      <th scope="col">Perdidas</th>
                      <th scope="col">Perdidas Justificadas</th>
                      <th scope="col">Validadas</th>
                      <th scope="col">Perdidas Validadas</th>
                      <th scope="col">CH Total</th>
                      <th scope="col">CH Validada</th>
                      <th scope="col">CH Perdida Validada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.taskResume.map((row, index) => (
                      <tr key={index}>
                        <td>{row.task}</td>
                        <td>{row.accomplished}</td>
                        <td>{row.lost}</td>
                        <td>{row.justifiedLost}</td>
                        <td>{row.validated}</td>
                        <td>{row.validatedLost}</td>
                        <td>{row.totalWorkload}</td>
                        <td>{row.validatedWorkload}</td>
                        <td>{row.lostValidatedWorkload}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <img src={noDataBg} alt="Sem dados" height={100} width={100} />
              </div>
            )}
          </AcademicOverviewCard>

          <AcademicOverviewCard cardTitle="Resumo por Local de Estágio">
            {/* <DataTable
              actions={[]}
              canSearch={data && data.locationResume.length > 0}
              columns={makeColumnsResumeByInternshipLocation()}
              data={data?.locationResume || []}
              loading={false}
              pagination={false}
            /> */}
            {data?.locationResume.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th scope="col">Local de Estagio</th>
                      <th scope="col">Realizadas</th>
                      <th scope="col">Perdidas</th>
                      <th scope="col">Perdidas Justificadas</th>
                      <th scope="col">Validadas</th>
                      <th scope="col">Perdidas Validadas</th>
                      <th scope="col">CH Total</th>
                      <th scope="col">CH Validada</th>
                      <th scope="col">CH Perdida Validada</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.locationResume.map((row, index) => (
                      <tr key={index}>
                        <td>{row.location}</td>
                        <td>{row.accomplished}</td>
                        <td>{row.lost}</td>
                        <td>{row.justifiedLost}</td>
                        <td>{row.validated}</td>
                        <td>{row.validatedLost}</td>
                        <td>{row.totalWorkload}</td>
                        <td>{row.validatedWorkload}</td>
                        <td>{row.lostValidatedWorkload}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <img src={noDataBg} alt="Sem dados" height={100} width={100} />
              </div>
            )}
          </AcademicOverviewCard>

          <AcademicOverviewCard cardTitle="Resumo por Profissional">
            {/* <DataTable
              actions={[]}
              canSearch={data && data.professionalResume.length > 0}
              columns={makeColumnsResumeByProfessional()}
              data={data?.professionalResume || []}
              loading={false}
              pagination={false}
            /> */}
            {data?.professionalResume.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th scope="col">Profissional</th>
                      <th scope="col">Atividades</th>
                      <th scope="col">Carga Horária</th>
                      <th scope="col">Avaliações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.professionalResume.map((row, index) => (
                      <tr key={index}>
                        <td>
                          <div className="d-flex my-2 gap-3 align-items-center">
                            <p className="mb-0">{row.name}</p>
                            <Badge>{row.profile}</Badge>
                          </div>
                        </td>
                        <td>{row.totalActivities}</td>
                        <td>{row.workload}</td>
                        <td>{row.ratings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="d-flex justify-content-center">
                <img src={noDataBg} alt="Sem dados" height={100} width={100} />
              </div>
            )}
          </AcademicOverviewCard>

          <AcademicOverviewCard cardTitle="Estudantes por Grupo">
            <StudentsByGroupChart data={data?.groupResume} />
          </AcademicOverviewCard>

          <AcademicOverviewCard cardTitle="Atividades por Dia">
            <ActivitiesByDayChart data={data?.dateActivities} />
          </AcademicOverviewCard>
        </>
      )}
    </PageWrapper>
  );
};
